import React from 'react';
import {
    Grid, TextField, FormControl, InputLabel, Select, MenuItem,
    InputAdornment, Tooltip, Button, Paper, Switch, FormControlLabel, Box, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BlockIcon from '@mui/icons-material/Block';
import ComputerIcon from '@mui/icons-material/Computer';
import SchoolIcon from '@mui/icons-material/School';
import { MONTH_NAMES } from './constants';

const FilterBar = ({
    filters,
    setFilters,
    ministerioOptions = ['all'],
    areaOptions = ['all'],
    plataformaOptions = ['all'],
    availableYears,
    handleClearFilters,
    isFilterActive,
    loading
}) => {
    const {
        ministerioFilter,
        areaFilter,
        nombreFilter,
        yearFilter,
        monthFilter,
        plataformaFilter = 'all',
        autogestionadoFilter = 'all',
        activosFilterActive,
        asignadoFilter,
        omitirCancelados
    } = filters;
    const {
        setMinisterioFilter,
        setAreaFilter,
        setNombreFilter,
        setYearFilter,
        setMonthFilter,
        setPlataformaFilter,
        setAutogestionadoFilter,
        setActivosFilterActive,
        setAsignadoFilter,
        setOmitirCancelados
    } = setFilters;

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 2, 
                mb: 3,
                opacity: loading ? 0.6 : 1,
                transition: 'opacity 0.3s ease',
                pointerEvents: loading ? 'none' : 'auto'
            }}
        >
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2.5}>
                    <TextField fullWidth label="Buscar por Nombre/Código" variant="outlined" size="small" value={nombreFilter} onChange={(e) => setNombreFilter(e.target.value)} disabled={loading} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>), }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                    <TextField fullWidth label="Buscar por Asignado" variant="outlined" size="small" value={asignadoFilter} onChange={(e) => setAsignadoFilter(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><AssignmentIndIcon color="action" fontSize="small" /></InputAdornment>), }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small" variant="outlined" disabled={loading || ministerioOptions.length <= 1}>
                        <InputLabel id="adm-filter-label">Ministerio</InputLabel>
                        <Select 
                            labelId="adm-filter-label" 
                            value={ministerioFilter} 
                            label="Ministerio" 
                            onChange={(e) => setMinisterioFilter(e.target.value)} 
                            startAdornment={<InputAdornment position="start" sx={{ ml: '-6px', mr: '4px' }}><AccountBalanceIcon color="action" fontSize='small' /></InputAdornment>} 
                            sx={{ 
                                '& .MuiSelect-select': { 
                                    pl: 1,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                } 
                            }} 
                        >
                            <MenuItem value="all"><em>Todos</em></MenuItem>
                            {ministerioOptions.filter(opt => opt !== 'all').map((opt, i) => (
                                <MenuItem 
                                    key={i} 
                                    value={opt}
                                    sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small" variant="outlined" disabled={loading || ministerioFilter === 'all' || areaOptions.length <= 1}>
                        <InputLabel id="area-filter-label">Área</InputLabel>
                        <Select 
                            labelId="area-filter-label" 
                            value={areaFilter} 
                            label="Área" 
                            onChange={(e) => setAreaFilter(e.target.value)} 
                            startAdornment={<InputAdornment position="start" sx={{ ml: '-6px', mr: '4px' }}><FolderSpecialIcon color="action" fontSize='small' /></InputAdornment>} 
                            sx={{ 
                                '& .MuiSelect-select': { 
                                    pl: 1,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                } 
                            }}
                        >
                            <MenuItem value="all"><em>Todas</em></MenuItem>
                            {areaOptions.filter(opt => opt !== 'all').map((opt, i) => (
                                <MenuItem 
                                    key={i} 
                                    value={opt}
                                    sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                    {opt}
                                </MenuItem>
                            ))}
                            {ministerioFilter !== 'all' && areaOptions.length <= 1 && (
                                <MenuItem value="all" disabled sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    <em>(Sin áreas)</em>
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={1.5}>
                    <FormControl fullWidth size="small" variant="outlined" disabled={loading}>
                        <InputLabel id="year-filter-label">Año</InputLabel>
                        <Select labelId="year-filter-label" value={yearFilter} label="Año" onChange={(e) => setYearFilter(e.target.value)} startAdornment={<InputAdornment position="start" sx={{ ml: '-6px', mr: '4px' }}><CalendarTodayIcon color="action" fontSize='small' /></InputAdornment>} sx={{ '& .MuiSelect-select': { pl: 1 } }}>
                            <MenuItem value="all"><em>Todos</em></MenuItem>
                            {(availableYears || []).map((y) => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={1.5}>
                    <FormControl fullWidth size="small" variant="outlined" disabled={loading}>
                        <InputLabel id="mes-filter-label">Mes</InputLabel>
                        <Select labelId="mes-filter-label" value={monthFilter} label="Mes" onChange={(e) => setMonthFilter(e.target.value)} startAdornment={<InputAdornment position="start" sx={{ ml: '-6px', mr: '4px' }}><CalendarMonthIcon color="action" fontSize='small' /></InputAdornment>} sx={{ '& .MuiSelect-select': { pl: 1 } }}>
                            <MenuItem value="all"><em>Todos</em></MenuItem>
                            {MONTH_NAMES.map((m, i) => (<MenuItem key={i} value={i.toString()}>{m}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small" variant="outlined" disabled={loading || plataformaOptions.length <= 1}>
                        <InputLabel id="plataforma-filter-label">Plataforma</InputLabel>
                        <Select
                            labelId="plataforma-filter-label"
                            value={plataformaFilter}
                            label="Plataforma"
                            onChange={(e) => setPlataformaFilter(e.target.value)}
                            startAdornment={<InputAdornment position="start" sx={{ ml: '-6px', mr: '4px' }}><ComputerIcon color="action" fontSize='small' /></InputAdornment>}
                            sx={{
                                '& .MuiSelect-select': {
                                    pl: 1,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        >
                            <MenuItem value="all"><em>Todas</em></MenuItem>
                            {plataformaOptions.filter(opt => opt !== 'all').map((opt, i) => (
                                <MenuItem key={i} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small" variant="outlined" disabled={loading}>
                        <InputLabel id="autogestionado-filter-label">Autogestionado</InputLabel>
                        <Select
                            labelId="autogestionado-filter-label"
                            value={autogestionadoFilter}
                            label="Autogestionado"
                            onChange={(e) => setAutogestionadoFilter(e.target.value)}
                            startAdornment={<InputAdornment position="start" sx={{ ml: '-6px', mr: '4px' }}><SchoolIcon color="action" fontSize='small' /></InputAdornment>}
                            sx={{ '& .MuiSelect-select': { pl: 1 } }}
                        >
                            <MenuItem value="all"><em>Todos</em></MenuItem>
                            <MenuItem value="si">Autogestionado (Sí)</MenuItem>
                            <MenuItem value="no">No autogestionado (No)</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex' }}>
                    <Tooltip title={activosFilterActive ? "Mostrar todos los cursos" : "Mostrar solo cursos activos ahora"}>
                        <Button fullWidth variant={activosFilterActive ? "contained" : "outlined"} size="medium" onClick={() => setActivosFilterActive(prev => !prev)} disabled={loading} startIcon={<AccessTimeIcon />} sx={{ height: '40px', minWidth: 'auto' }}>Activos</Button>
                    </Tooltip>
                </Grid>
                <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex' }}>
                    <Button fullWidth variant="outlined" size="medium" onClick={handleClearFilters} disabled={!isFilterActive || loading} startIcon={<ClearAllIcon />} sx={{ height: '40px', minWidth: 'auto' }}>Limpiar</Button>
                </Grid>
                <Grid item xs={12} sm={12} md={5} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, ml: 'auto' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={omitirCancelados}
                                onChange={(e) => setOmitirCancelados(e.target.checked)}
                                color="secondary"
                                size="small"
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <BlockIcon fontSize="small" color={omitirCancelados ? 'secondary' : 'action'} />
                                <Typography variant="body2" sx={{ fontWeight: omitirCancelados ? 600 : 400, color: omitirCancelados ? 'secondary.main' : 'text.secondary' }}>
                                    Omitir Cancelados
                                </Typography>
                            </Box>
                        }
                        sx={{ mr: 0 }}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

export default FilterBar;
